import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import Common from "../../../Components/Layouts/Common.js";
import { useLocation, useNavigate } from "react-router-dom";
import useUnifiedAPI from "../../../Hooks/useApi.js";
import { Button } from "@mui/material";
import showToast from "../../../Helpers/toast.js";
import { CircularProgress } from "@mui/material";

const SurveyDataSubmission = () => {
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [fileSuccess, setFileSuccess] = useState("");
  const { request, loading } = useUnifiedAPI();

  const location = useLocation();
  const { surveyId, questionId, surveyName, answerType, locationId, city } =
    location?.state || {};

  const navigate = useNavigate();

  // File drop handling
  const onDrop = (acceptedFiles) => {
    handleFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
    multiple: false,
  });

  // Validate and set file
  const handleFile = (selectedFile) => {
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (validTypes.includes(selectedFile.type)) {
      setFile(selectedFile);
      setFileError("");
      setFileSuccess(`File "${selectedFile.name}" selected`);
    } else {
      setFile(null);
      setFileError("Please upload only Excel files (.xls or .xlsx)");
      setFileSuccess("");
    }
  };

  // Handle file upload
  const handleFileUpload = async () => {
    if (!file) {
      setFileError("No file selected. Please choose a file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file, file.name);
    const bodyParam = encodeURIComponent(
      JSON.stringify({
        surveyId: surveyId,
        questionnaireId: questionId,
        answerType: `${answerType}`,
        locationId: locationId,
      })
    );

    try {
      const response = await request(
        "POST",
        `/v1/survey/questions/answers/upload?body=${bodyParam}`,
        {
          data: formData,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
        "survey_service"
      );

      if (response) {
        showToast({ message: "File Uploaded successfully" });
        // setTimeout(() => {
        //   navigate("/survey-data-submission-city-list", {
        //     surveyId: surveyId,
        //     surveyName: surveyName,
        //     questionId: questionId,
        //   });
        // });
        setFileSuccess("File uploaded successfully!");
        setFileError("");
      } else {
        setFileError("Error uploading file. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setFileError("Error uploading file. Please try again.");
    }
  };

  useEffect(() => {
    if (!surveyId) {
      navigate("/manage-survey");
    }
  }, [surveyId]);

  return (
    <Common headline={"Submit Survey Data"}>
      <div className="space-y-6">
        <div className="flex mb-4 ">
          <h4 className=" font-bold text-gray-800 pr-2">Survey Name:</h4>
          <h5>{surveyName}</h5>
        </div>
        <div className="flex mb-4 ">
          <h4 className=" font-bold text-gray-800 pr-2">City Name:</h4>
          <h5>{city}</h5>
        </div>
        <div
          {...getRootProps()}
          className="p-2 flex justify-center items-center h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors"
        >
          <input {...getInputProps()} />
          <p>
            {isDragActive
              ? "Drop the file here"
              : "Drag 'n' drop an Excel file here, or click to select a file"}
          </p>
        </div>
        {fileError && <p className="text-red-500">{fileError}</p>}
        {fileSuccess && <p className="text-green-500">{fileSuccess}</p>}
        <div className="flex justify-center gap-3">
          {loading && <CircularProgress size={30} />}
          <Button
            variant="contained"
            color="primary"
            disabled={!file || loading}
            onClick={handleFileUpload}
            className={`${loading ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            Upload and Proceed
          </Button>
        </div>
      </div>
    </Common>
  );
};

export default SurveyDataSubmission;
